// components/ui/Input.tsx - Enhanced optimized input component with backward compatibility
import React, { forwardRef, ChangeEvent } from 'react';
import { Controller, Control } from 'react-hook-form';
import { cn } from '@/utils/cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface BaseInputProps {
  // New optimized API
  variant?: 'default' | 'filled' | 'flushed';
  inputSize?: 'sm' | 'md' | 'lg';
  helperText?: string;
  label?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;

  // Legacy compatibility props from InputField
  id: string;
  name: string;
  required?: boolean;
  className?: string;
  labelClass?: string;
  error?: string | boolean;
  control?: Control<any>;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  hideLabel?: boolean;
  isMulti?: boolean;
}

interface CheckboxGroupInputProps extends BaseInputProps {
  type: 'checkbox-group';
  value?: string[];
  defaultValue?: string[];
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  checkboxOptions: Array<{ value: string; label: string }>;
}

interface CheckboxInputProps extends BaseInputProps {
  type: 'checkbox';
  value?: boolean;
  defaultValue?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  checkboxLabel?: string;
}

interface RadioInputProps extends BaseInputProps {
  type: 'radio';
  value?: string | number;
  defaultValue?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  radioOptions: Array<{ value: string; label: string }>;
}

interface SelectInputProps extends BaseInputProps {
  type: 'select';
  value?: string | number;
  defaultValue?: string;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: () => void;
  options: Array<{ value: string; label: string }>;
}

interface TextareaInputProps extends BaseInputProps {
  type: 'textarea';
  value?: string | number;
  defaultValue?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  rows?: number;
}

interface RegularInputProps extends BaseInputProps {
  type?: 'text' | 'number' | 'date' | 'datetime-local' | 'email' | 'tel' | 'hidden' | 'password';
  value?: string | number;
  defaultValue?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  min?: string | number;
  max?: string | number;
}

export type InputProps = 
  | CheckboxGroupInputProps 
  | CheckboxInputProps 
  | RadioInputProps 
  | SelectInputProps 
  | TextareaInputProps 
  | RegularInputProps;

const inputVariants = {
  default: 'border-2 border-gray-300 focus:border-brandColor dark:border-gray-600 bg-white dark:bg-gray-800',
  filled: 'border-0 bg-gray-100 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600',
  flushed: 'border-0 border-b-2 border-gray-300 dark:border-gray-600 rounded-none bg-transparent focus:border-brandColor'
};

const inputSizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-4 py-3 text-base'
};

export const Input = forwardRef<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  InputProps
>(({
  className = '',
  variant = 'default',
  inputSize = 'md',
  error,
  helperText,
  label,
  leftIcon,
  rightIcon,
  hideLabel = false,
  required = false,
  labelClass = '',
  control,
  disabled = false,
  ...props
}, ref) => {
  // Handle both string and boolean error types for backward compatibility
  const hasError = typeof error === 'string' ? !!error : !!error;
  const errorMessage = typeof error === 'string' ? error : helperText;

  const baseInputClasses = cn(
    'block w-full rounded-lg font-medium transition-all duration-200',
    'text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400',
    'focus:outline-none focus:ring-2 focus:ring-brandColor/20',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100',
    inputVariants[variant],
    inputSizes[inputSize],
    leftIcon && 'pl-10',
    rightIcon && 'pr-10',
    hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
  );

  const wrapperClasses = cn('w-full', className);

  const renderLabel = () => {
    if (hideLabel || !label) return null;

    return (
      <label 
        htmlFor={props.id} 
        className={cn(
          'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1',
          labelClass
        )}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    );
  };

  const renderError = () => {
    if (!errorMessage) return null;
    return <p className="mt-1 text-xs text-red-600">{errorMessage}</p>;
  };

  const renderIcons = (children: React.ReactNode) => {
    if (!leftIcon && !rightIcon) return children;

    return (
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            {leftIcon}
          </div>
        )}
        {children}
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            {rightIcon}
          </div>
        )}
      </div>
    );
  };

  const renderControlledInput = (field: any): React.ReactElement => {
    if (props.type === 'checkbox-group' && 'checkboxOptions' in props) {
      return (
        <div className="mt-1 space-x-4 flex flex-wrap">
          {props.checkboxOptions.map((option, index) => (
            <div key={`${props.id}-${index}`} className="flex items-center">
              <input
                type="checkbox"
                id={`${props.id}-${index}`}
                value={option.value}
                checked={Array.isArray(field.value) ? field.value.includes(option.value) : false}
                onChange={(e) => {
                  if (disabled) return;
                  const updatedValue = Array.isArray(field.value) ? [...field.value] : [];
                  if (e.target.checked) {
                    updatedValue.push(option.value);
                  } else {
                    const idx = updatedValue.indexOf(option.value);
                    if (idx > -1) updatedValue.splice(idx, 1);
                  }
                  field.onChange(updatedValue);
                }}
                disabled={disabled}
                className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
              />
              <label htmlFor={`${props.id}-${index}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </label>
            </div>
          ))}
        </div>
      );
    }

    if (props.type === 'checkbox' && 'checkboxLabel' in props) {
      return (
        <div className="flex items-center mt-1">
          <input
            type="checkbox"
            id={props.id}
            checked={field.value || false}
            onChange={(e) => field.onChange(e.target.checked)}
            disabled={disabled}
            className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
          />
          {props.checkboxLabel && (
            <label htmlFor={props.id} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              {props.checkboxLabel}
            </label>
          )}
        </div>
      );
    }

    if (props.type === 'radio' && 'radioOptions' in props) {
      return (
        <div className="mt-1 space-x-4 flex flex-wrap">
          {props.radioOptions.map((option) => (
            <div key={`${props.id}-${option.value}`} className="flex items-center">
              <input
                type="radio"
                id={`${props.id}-${option.value}`}
                {...field}
                value={option.value}
                checked={field.value === option.value}
                disabled={disabled}
                className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300"
              />
              <label htmlFor={`${props.id}-${option.value}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {option.label}
              </label>
            </div>
          ))}
        </div>
      );
    }

    if (props.type === 'select' && 'options' in props) {
      return renderIcons(
        <select
          {...field}
          id={props.id}
          disabled={disabled}
          className={cn(baseInputClasses, 'flex-1')}
          ref={ref as React.Ref<HTMLSelectElement>}
        >
          <option value="">Select an option</option>
          {props.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) as React.ReactElement;
    }

    if (props.type === 'textarea') {
      return renderIcons(
        <textarea
          {...field}
          id={props.id}
          placeholder={props.placeholder}
          maxLength={props.maxLength}
          disabled={disabled}
          rows={'rows' in props ? props.rows : 4}
          className={cn(baseInputClasses, 'resize-y flex-1')}
          autoComplete="off"
          ref={ref as React.Ref<HTMLTextAreaElement>}
        />
      ) as React.ReactElement;
    }

    // Regular input types
    return renderIcons(
      <input
        {...field}
        type={props.type || 'text'}
        id={props.id}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        disabled={disabled}
        className={cn(baseInputClasses, 'flex-1')}
        {...(props.type === 'number' && {
          min: props.min,
          max: props.max,
        })}
        autoComplete="off"
        ref={ref as React.Ref<HTMLInputElement>}
      />
    ) as React.ReactElement;
  };

  if (control) {
    return (
      <div className={wrapperClasses}>
        {renderLabel()}
        <Controller
          name={props.name}
          control={control}
          render={({ field }) => renderControlledInput(field)}
        />
        {renderError()}
      </div>
    );
  }

  // Uncontrolled version for backward compatibility
  return (
    <div className={wrapperClasses}>
      {renderLabel()}
      {props.type === 'textarea' ? (
        renderIcons(
          <textarea
            id={props.id}
            name={props.name}
            value={props.value as string}
            onChange={props.onChange as any}
            onBlur={props.onBlur}
            required={required}
            placeholder={props.placeholder}
            maxLength={props.maxLength}
            disabled={disabled}
            rows={'rows' in props ? props.rows : 4}
            className={cn(baseInputClasses, 'resize-y flex-1')}
            defaultValue={props.defaultValue as string}
            autoComplete="off"
            ref={ref as React.Ref<HTMLTextAreaElement>}
          />
        )
      ) : props.type === 'select' && 'options' in props ? (
        renderIcons(
          <select
            id={props.id}
            name={props.name}
            value={props.value as string}
            onChange={props.onChange as any}
            onBlur={props.onBlur}
            required={required}
            disabled={disabled}
            className={cn(baseInputClasses, 'flex-1')}
            defaultValue={props.defaultValue}
            ref={ref as React.Ref<HTMLSelectElement>}
          >
            <option value="">Select an option</option>
            {props.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )
      ) : (
        renderIcons(
          <input
            type={props.type || 'text'}
            id={props.id}
            name={props.name}
            value={props.value as string}
            onChange={props.onChange as any}
            onBlur={props.onBlur}
            required={required}
            placeholder={props.placeholder}
            maxLength={props.maxLength}
            disabled={disabled}
            className={cn(baseInputClasses, 'flex-1')}
            defaultValue={props.defaultValue as string}
            {...(props.type === 'number' && {
              min: props.min,
              max: props.max,
            })}
            autoComplete="off"
            ref={ref as React.Ref<HTMLInputElement>}
          />
        )
      )}
      {renderError()}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;